import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import {
  Button,
  Divider,
  Grid,
  Header,
  Input,
  Segment,
  Table,
  Dropdown,
  Checkbox,
  Tab,
  Icon
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { history } from '../../_helpers';
import { useCurrentUser } from '../../_services/hooks/user';
import { servicesAPI } from '../../_services';
import {
  copyToClipboard,
  dbUTCDatetimeToLocal,
  brandedCustomerType
} from '../../utils';

const statusOptions = [
  { text: 'Prospective', value: 'Prospective' },
  { text: 'Signup', value: 'Signup' },
  { text: 'Active', value: 'Active' },
  { text: 'Inactive', value: 'Inactive' }
];

const preSurveyColour = '#CCE5FF';
const postSurveyColour = '#99CCFF';

export const ListCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [customersVehicles, setCustomersVehicles] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [aonSignups, setAonSignUps] = useState(null);
  const [applyFilter, setApplyFilter] = useState(false);
  const [forenameFilter, setForenameFilter] = useState('');
  const [surnameFilter, setSurnameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [registrationsFilter, setRegistrationsFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState(['Signup', 'Active']);
  const [showTestAccounts, setShowTestAccounts] = useState(false);
  const [showInactiveAccounts, setShowInactiveAccounts] = useState(true);
  const [showCompletedSignups, setShowCompletedSignups] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortLabel, setSortLabel] = useState('descending');
  const [sortedData, setSortedData] = useState([]);
  const [
    activeCustomersWithNoLances,
    setActiveCustomersWithNoLances
  ] = useState([]);
  const [duplicateCustomers, setDuplicateCustomers] = useState([]);

  const { currentUserCan, currentUserIsDebug } = useCurrentUser();

  const sortTable = column => {
    setSortColumn(column);
    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    setSortLabel(sortLabel === 'ascending' ? 'descending' : 'ascending');
  };

  useEffect(() => {
    servicesAPI
      .CustomerServices('getCustomersVehicles')
      .then(customersVehicles => setCustomersVehicles(customersVehicles));

    servicesAPI.CustomerServices().then(customers => {
      setCustomers(
        customers.map(c => ({
          id: c.id,
          app_user: c.app_user,
          created_datetime: c.created_datetime,
          customer_type: c.customer_type,
          email: c.email,
          forename: c.forename,
          home_tel_no: c.home_tel_no,
          mob_tel_no: c.mob_tel_no,
          status: c.status,
          surname: c.surname,
          test_account: c.test_account
        }))
      );
    });

    servicesAPI
      .AdminServices('getStatesByEntityType', 'AON_SIGN_UP')
      .then(aonSignups => {
        let newAonSignups = [];
        for (const id of Object.keys(aonSignups)) {
          const signup = JSON.parse(aonSignups[id]);

          const preSurveyCompleted =
            signup.hosting_type !== null &&
            signup.stripe_card_setup &&
            (signup.survey_completed || signup.self_survey === false) &&
            signup.flatpeak_id &&
            signup.charging_type &&
            signup.agreed_to_terms &&
            signup.initial_payment_submitted;

          const postSurveyCompleted =
            signup.approved_for_aon &&
            signup.installation_supplemental_payment_submitted &&
            signup.stripe_connect_setup;

          newAonSignups.push({
            ...signup,
            customer_id: id,
            pre_survey_completed: preSurveyCompleted,
            post_survey_completed: postSurveyCompleted
          });
        }
        setAonSignUps(newAonSignups);
      });

    sortTable('created_datetime');
    setSortDirection('desc');
    setSortLabel('descending');
  }, []);

  useEffect(() => {
    if (
      customers.length === 0 ||
      customersVehicles.length === 0 ||
      aonSignups === null
    )
      return;

    let filteredCustomers = customers
      .filter(
        ({ forename }) =>
          !forenameFilter || forename.toLowerCase().includes(forenameFilter)
      )
      .filter(
        ({ surname }) =>
          !surnameFilter || surname.toLowerCase().includes(surnameFilter)
      )
      .filter(
        ({ email }) => !emailFilter || email.toLowerCase().includes(emailFilter)
      )
      .filter(
        ({ home_tel_no, mob_tel_no }) =>
          !phoneFilter ||
          home_tel_no.toLowerCase().includes(phoneFilter) ||
          mob_tel_no.toLowerCase().includes(phoneFilter)
      )
      .filter(
        ({ status }) => !statusFilter.length || statusFilter.includes(status)
      )
      .filter(
        ({ test_account }) =>
          (showTestAccounts && test_account) || !test_account
      )
      .filter(({ status }) => showInactiveAccounts || status === 'Active')
      .filter(
        ({ pre_survey_completed }) =>
          !showCompletedSignups || pre_survey_completed
      )
      .filter(
        ({ customer_type }) =>
          !typeFilter ||
          brandedCustomerType(customer_type)
            .toLowerCase()
            .includes(typeFilter)
      );

    const customersAndVehicles = connectVehiclesToCustomers(filteredCustomers);

    filteredCustomers = customersAndVehicles.filter(
      ({ registrations }) =>
        !registrationsFilter ||
        registrations.toLowerCase().includes(registrationsFilter)
    );

    // parse customersVehicles and set c.vehicles
    // to a comma-separated string of regs

    const sortedCustomers =
      sortColumn && sortDirection
        ? _.orderBy(filteredCustomers, sortColumn, sortDirection)
        : filteredCustomers;

    setSortedData(sortedCustomers);
  }, [
    customers,
    customersVehicles,
    aonSignups,
    applyFilter,
    // forenameFilter,
    // surnameFilter,
    // emailFilter,
    // phoneFilter,
    // registrationsFilter,
    // statusFilter,
    // typeFilter,
    // showTestAccounts,
    // showInactiveAccounts,
    // showCompletedSignups,
    sortColumn,
    sortDirection
  ]);

  useEffect(() => {
    if (customers.length === 0 || customersVehicles.length === 0) return;

    if (tabIndex === 2 && activeCustomersWithNoLances.length === 0) {
      servicesAPI
        .AdminServices('getReportCustomersAwaitingLances')
        .then(customersAwaitingLances => {
          const activeCustomersWithNoLances = customers.filter(c =>
            customersAwaitingLances.includes(c.id)
          );
          const sortedActiveCustomersWithNoLances = _.orderBy(
            activeCustomersWithNoLances,
            'created_datetime',
            'desc'
          );

          setActiveCustomersWithNoLances(sortedActiveCustomersWithNoLances);
        });
    } else if (tabIndex === 3 && duplicateCustomers.length === 0) {
      servicesAPI
        .AdminServices('getReportDuplicateCustomers')
        .then(duplicateCustomers => {
          setDuplicateCustomers(duplicateCustomers);
        });
    }
  }, [customers, customersVehicles, tabIndex]);

  const connectVehiclesToCustomers = customers =>
    customers.map(c => {
      let aonSignup = aonSignups.find(a => a.customer_id === c.id);
      if (!aonSignup) aonSignup = {};

      const registrations = customersVehicles
        .filter(v => v.customer_id === c.id)
        .map(v => v.registration_no.toUpperCase().replace(/\s/g, ''));

      return {
        ...c,
        app_user: c.app_user ? 'Yes' : 'No',
        registrations: registrations.toString().replace(/,/g, ' '),
        ...aonSignup
      };
    });

  const handleTabChange = (e, { activeIndex }) => setTabIndex(activeIndex);

  return (
    <Segment>
      <Tab
        menu={{ secondary: true, pointing: true, tabular: false }}
        onTabChange={handleTabChange}
        panes={[
          {
            menuItem: 'Customers',
            render: () => (
              <Tab.Pane attached={false}>
                <Grid stretched>
                  <Grid.Row>
                    <Grid.Column>
                      <Header as="h2">Customers</Header>
                    </Grid.Column>
                    <Grid.Column
                      floated="right"
                      textAlign="right"
                      mobile={6}
                      tablet={4}
                      computer={3}
                    >
                      <Button as={Link} color="olive" to={`/admin/customer/0`}>
                        Create Customer
                      </Button>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Input
                        type="text"
                        placeholder="filter forename"
                        icon={{
                          name: 'close',
                          link: true,
                          onClick: () => {
                            setForenameFilter('');
                          }
                        }}
                        value={forenameFilter}
                        onChange={(event, { value }) => {
                          let filter = null;
                          if (value)
                            filter = value.toLowerCase().replace(/\s/g, '');
                          setForenameFilter(filter);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Input
                        type="text"
                        placeholder="filter surname"
                        icon={{
                          name: 'close',
                          link: true,
                          onClick: () => {
                            setSurnameFilter('');
                          }
                        }}
                        value={surnameFilter}
                        onChange={(event, { value }) => {
                          let filter = null;
                          if (value)
                            filter = value.toLowerCase().replace(/\s/g, '');
                          setSurnameFilter(filter);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Input
                        type="text"
                        placeholder="filter email address"
                        icon={{
                          name: 'close',
                          link: true,
                          onClick: () => {
                            setEmailFilter('');
                          }
                        }}
                        value={emailFilter}
                        onChange={(event, { value }) => {
                          let filter = null;
                          if (value)
                            filter = value.toLowerCase().replace(/\s/g, '');
                          setEmailFilter(filter);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Input
                        type="text"
                        placeholder="filter landline/mobile number"
                        icon={{
                          name: 'close',
                          link: true,
                          onClick: () => {
                            setPhoneFilter('');
                          }
                        }}
                        value={phoneFilter}
                        onChange={(event, { value }) => {
                          let filter = null;
                          if (value)
                            filter = value.toLowerCase().replace(/\s/g, '');
                          setPhoneFilter(filter);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Input
                        type="text"
                        placeholder="filter registrations"
                        icon={{
                          name: 'close',
                          link: true,
                          onClick: () => {
                            setRegistrationsFilter('');
                          }
                        }}
                        value={registrationsFilter}
                        onChange={(event, { value }) => {
                          let filter = null;
                          if (value)
                            filter = value.toLowerCase().replace(/\s/g, '');
                          setRegistrationsFilter(filter);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Dropdown
                        placeholder="filter status"
                        selection
                        search
                        options={statusOptions}
                        multiple
                        onChange={(e, inputProps) => {
                          setStatusFilter(inputProps.value);
                        }}
                        value={statusFilter}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Input
                        type="text"
                        placeholder="filter type"
                        icon={{
                          name: 'close',
                          link: true,
                          onClick: () => {
                            setTypeFilter('');
                          }
                        }}
                        value={typeFilter}
                        onChange={(event, { value }) => {
                          let filter = null;
                          if (value)
                            filter = value.toLowerCase().replace(/\s/g, '');
                          setTypeFilter(filter);
                        }}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Checkbox
                        toggle
                        label="Show test accounts"
                        checked={showTestAccounts}
                        onChange={e => setShowTestAccounts(!showTestAccounts)}
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                <br />
                <Grid.Row>
                  <Grid.Column>
                    <Button
                      color="olive"
                      onClick={() => setApplyFilter(!applyFilter)}
                    >
                      Apply Filters
                    </Button>
                  </Grid.Column>
                </Grid.Row>
                <Divider hidden />
                <Grid>
                  <Table sortable celled fixed>
                    <Table.Header>
                      <Table.Row>
                        {currentUserIsDebug ? (
                          <Table.HeaderCell style={{ width: '120px' }}>
                            ID
                          </Table.HeaderCell>
                        ) : (
                          ''
                        )}
                        <Table.HeaderCell
                          sorted={sortColumn === 'forename' ? sortLabel : null}
                          onClick={() => sortTable('forename')}
                          style={{ width: '120px' }}
                        >
                          Forename
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          sorted={sortColumn === 'surname' ? sortLabel : null}
                          onClick={() => sortTable('surname')}
                          style={{ width: '120px' }}
                        >
                          Surname
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          sorted={sortColumn === 'email' ? sortLabel : null}
                          onClick={() => sortTable('email')}
                          style={{ width: '130px' }}
                        >
                          Email
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          sorted={
                            sortColumn === 'registrations' ? sortLabel : null
                          }
                          onClick={() => sortTable('registrations')}
                          style={{ width: '110px' }}
                        >
                          Registrations
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          sorted={
                            sortColumn === 'mob_tel_no' ? sortLabel : null
                          }
                          onClick={() => sortTable('mob_tel_no')}
                          style={{ width: '120px' }}
                        >
                          Mobile no.
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          sorted={sortColumn === 'status' ? sortLabel : null}
                          onClick={() => sortTable('status')}
                          style={{ width: '80px' }}
                        >
                          Status
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          sorted={
                            sortColumn === 'created_datetime' ? sortLabel : null
                          }
                          onClick={() => sortTable('created_datetime')}
                          style={{ width: '160px' }}
                        >
                          Signup timestamp
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          sorted={
                            sortColumn === 'customer_type' ? sortLabel : null
                          }
                          onClick={() => sortTable('customer_type')}
                          style={{ width: '110px' }}
                        >
                          Type
                        </Table.HeaderCell>
                        <Table.HeaderCell
                          sorted={sortColumn === 'app_user' ? sortLabel : null}
                          onClick={() => sortTable('app_user')}
                          style={{ width: '75px' }}
                        >
                          App user
                        </Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {sortedData.map((d, idx) => (
                        <Table.Row
                          key={idx}
                          onClick={() => {
                            copyToClipboard(d.id);
                            history.push(`/admin/customer/${d.id}`);
                          }}
                        >
                          {currentUserIsDebug ? (
                            <Table.Cell>{d.id}</Table.Cell>
                          ) : (
                            ''
                          )}
                          <Table.Cell>{d.forename}</Table.Cell>
                          <Table.Cell>{d.surname}</Table.Cell>
                          <Table.Cell>{d.email}</Table.Cell>
                          <Table.Cell>{d.registrations}</Table.Cell>
                          <Table.Cell>
                            <a href={'tel:' + d.mob_tel_no}>{d.mob_tel_no}</a>
                          </Table.Cell>
                          <Table.Cell>{d.status}</Table.Cell>
                          <Table.Cell>
                            {dbUTCDatetimeToLocal(d.created_datetime)}
                          </Table.Cell>
                          <Table.Cell>
                            {brandedCustomerType(d.customer_type)}
                          </Table.Cell>
                          <Table.Cell>{d.app_user}</Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </Grid>
              </Tab.Pane>
            )
          },
          {
            menuItem: 'HOME Customers',
            render: () => (
              <Tab.Pane attached={false}>
                <Grid stretched>
                  <Grid.Row>
                    <Grid.Column>
                      <Header as="h2">HOME Customers</Header>
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Input
                        type="text"
                        placeholder="filter forename"
                        icon={{
                          name: 'close',
                          link: true,
                          onClick: () => {
                            setForenameFilter('');
                          }
                        }}
                        value={forenameFilter}
                        onChange={(event, { value }) => {
                          let filter = null;
                          if (value)
                            filter = value.toLowerCase().replace(/\s/g, '');
                          setForenameFilter(filter);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Input
                        type="text"
                        placeholder="filter surname"
                        icon={{
                          name: 'close',
                          link: true,
                          onClick: () => {
                            setSurnameFilter('');
                          }
                        }}
                        value={surnameFilter}
                        onChange={(event, { value }) => {
                          let filter = null;
                          if (value)
                            filter = value.toLowerCase().replace(/\s/g, '');
                          setSurnameFilter(filter);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Input
                        type="text"
                        placeholder="filter email address"
                        icon={{
                          name: 'close',
                          link: true,
                          onClick: () => {
                            setEmailFilter('');
                          }
                        }}
                        value={emailFilter}
                        onChange={(event, { value }) => {
                          let filter = null;
                          if (value)
                            filter = value.toLowerCase().replace(/\s/g, '');
                          setEmailFilter(filter);
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column mobile={8} tablet={6} computer={4}>
                      <Dropdown
                        placeholder="filter status"
                        selection
                        search
                        options={statusOptions}
                        multiple
                        onChange={(e, inputProps) => {
                          setStatusFilter(inputProps.value);
                        }}
                        value={statusFilter}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Checkbox
                        toggle
                        label="Show test accounts"
                        checked={showTestAccounts}
                        onChange={e => setShowTestAccounts(!showTestAccounts)}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Checkbox
                        toggle
                        label="Show customers who have not activated their account"
                        checked={showInactiveAccounts}
                        onChange={e =>
                          setShowInactiveAccounts(!showInactiveAccounts)
                        }
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>
                      <Checkbox
                        toggle
                        label="Show only completed sign-ups"
                        checked={showCompletedSignups}
                        onChange={e =>
                          setShowCompletedSignups(!showCompletedSignups)
                        }
                      />
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
                <br />
                <Grid.Row>
                  <Grid.Column>
                    <Button
                      color="olive"
                      onClick={() => setApplyFilter(!applyFilter)}
                    >
                      Apply Filters
                    </Button>
                  </Grid.Column>
                </Grid.Row>
                <Divider hidden />
                <div>
                  <b>Key:</b>
                </div>
                <div style={{ width: '120px', background: preSurveyColour }}>
                  Pre-survey steps
                </div>
                <div style={{ width: '120px', background: postSurveyColour }}>
                  Post-survey steps
                </div>
                <br />
                <br />
                <Grid>
                  <div role="region" aria-labelledby="caption" tabindex="0">
                    <table className="scrollable">
                      <thead>
                        <tr>
                          <th
                            sorted={
                              sortColumn === 'forename' ? sortLabel : null
                            }
                            onClick={() => sortTable('forename')}
                            style={{ background: 'white' }}
                          >
                            Forename
                          </th>
                          <th
                            sorted={sortColumn === 'surname' ? sortLabel : null}
                            onClick={() => sortTable('surname')}
                            style={{ background: 'white' }}
                          >
                            Surname
                          </th>
                          <th
                            sorted={sortColumn === 'email' ? sortLabel : null}
                            onClick={() => sortTable('email')}
                            style={{ background: 'white' }}
                          >
                            Email
                          </th>
                          <th
                            sorted={sortColumn === 'status' ? sortLabel : null}
                            onClick={() => sortTable('status')}
                            style={{ background: 'white' }}
                          >
                            Status
                          </th>
                          <th
                            sorted={
                              sortColumn === 'created_datetime'
                                ? sortLabel
                                : null
                            }
                            onClick={() => sortTable('created_datetime')}
                            style={{ background: 'white' }}
                          >
                            Signup
                            <br />
                            timestamp
                          </th>

                          <th
                            sorted={
                              sortColumn === 'self_survey' ? sortLabel : null
                            }
                            onClick={() => sortTable('self_survey')}
                            style={{ background: 'white' }}
                          >
                            Survey
                            <br />
                            performed
                            <br />
                            by
                          </th>
                          <th
                            style={{
                              background: preSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Selected
                            <br />
                            Offer
                          </th>
                          <th
                            style={{
                              background: preSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Payment
                            <br />
                            Card
                            <br />
                            Added
                          </th>
                          <th
                            style={{
                              background: preSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Survey
                            <br />
                            Completed
                          </th>
                          <th
                            style={{
                              background: preSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Flatpeak
                            <br />
                            Synced
                          </th>
                          <th
                            style={{
                              background: preSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Charging
                            <br />
                            Pref's
                            <br />
                            Chosen
                          </th>
                          <th
                            style={{
                              background: preSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Agreed
                            <br />
                            To
                            <br />
                            T&Cs
                          </th>
                          <th
                            style={{
                              background: preSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Initial
                            <br />
                            Payment
                            <br />
                            Paid
                          </th>
                          <th
                            style={{
                              background: postSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Survey
                            <br />
                            Approved
                          </th>
                          <th
                            style={{
                              background: postSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Supplemental
                            <br />
                            Payment
                            <br />
                            Paid
                          </th>
                          <th
                            style={{
                              background: postSurveyColour,
                              fontSize: '9px'
                            }}
                          >
                            Bank
                            <br />
                            Account
                            <br />
                            Details
                            <br />
                            Added
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedData
                          .filter(
                            d => brandedCustomerType(d.customer_type) === 'HOME'
                          )
                          .map((d, idx) => (
                            <tr
                              key={idx}
                              onClick={() => {
                                copyToClipboard(d.id);
                                history.push(`/admin/customer/${d.id}`);
                              }}
                            >
                              <td
                                className={
                                  d.pre_survey_completed
                                    ? 'aon-success left'
                                    : 'left'
                                }
                              >
                                {d.forename}
                              </td>
                              <td
                                className={
                                  d.pre_survey_completed
                                    ? 'aon-success left'
                                    : 'left'
                                }
                              >
                                {d.surname}
                              </td>
                              <td
                                className={
                                  d.pre_survey_completed
                                    ? 'aon-success left'
                                    : 'left'
                                }
                              >
                                {d.email}
                              </td>
                              <td
                                className={
                                  d.pre_survey_completed ? 'aon-success' : ''
                                }
                              >
                                {d.status}
                              </td>
                              <td
                                className={
                                  d.pre_survey_completed ? 'aon-success' : ''
                                }
                              >
                                {dbUTCDatetimeToLocal(d.created_datetime)}
                              </td>

                              {d.status === 'Active' ? (
                                <>
                                  <td
                                    className={
                                      d.pre_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.self_survey === false
                                      ? 'Trojan'
                                      : d.self_survey === true
                                      ? 'Customer'
                                      : '?'}
                                  </td>
                                  <td
                                    className={
                                      d.pre_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.hosting_type !== null ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                  <td
                                    className={
                                      d.pre_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.stripe_card_setup ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                  <td
                                    className={
                                      d.pre_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.survey_completed ||
                                    d.self_survey === false ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                  <td
                                    className={
                                      d.pre_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.flatpeak_id ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                  <td
                                    className={
                                      d.pre_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.charging_type ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                  <td
                                    className={
                                      d.pre_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.agreed_to_terms ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                  <td
                                    className={
                                      d.pre_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.initial_payment_submitted ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                  <td
                                    className={
                                      d.post_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.approved_for_aon ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                  <td
                                    className={
                                      d.post_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.installation_supplemental_payment_paid ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                  <td
                                    className={
                                      d.post_survey_completed
                                        ? 'aon-success'
                                        : ''
                                    }
                                  >
                                    {d.stripe_connect_setup ? (
                                      <Icon color="green" name="checkmark" />
                                    ) : (
                                      <Icon color="red" name="x icon" />
                                    )}
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td />
                                  <td />
                                  <td />
                                  <td />
                                  <td />
                                  <td />
                                  <td />
                                  <td />
                                  <td />
                                  <td />
                                  <td />
                                </>
                              )}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </Grid>
              </Tab.Pane>
            )
          },
          currentUserCan('ManageCustomer')
            ? {
                menuItem: 'Customers Awaiting Lances',
                render: () => (
                  <Tab.Pane attached={false}>
                    <Grid>
                      <Table sortable celled fixed>
                        <Table.Header>
                          <Table.Row>
                            {currentUserIsDebug ? (
                              <Table.HeaderCell style={{ width: '120px' }}>
                                ID
                              </Table.HeaderCell>
                            ) : (
                              ''
                            )}
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'forename' ? sortLabel : null
                              }
                              onClick={() => sortTable('forename')}
                              style={{ width: '120px' }}
                            >
                              Forename
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'surname' ? sortLabel : null
                              }
                              onClick={() => sortTable('surname')}
                              style={{ width: '120px' }}
                            >
                              Surname
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={sortColumn === 'email' ? sortLabel : null}
                              onClick={() => sortTable('email')}
                              style={{ width: '130px' }}
                            >
                              Email
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'registrations'
                                  ? sortLabel
                                  : null
                              }
                              onClick={() => sortTable('registrations')}
                              style={{ width: '110px' }}
                            >
                              Registrations
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'mob_tel_no' ? sortLabel : null
                              }
                              onClick={() => sortTable('mob_tel_no')}
                              style={{ width: '120px' }}
                            >
                              Mobile no.
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'status' ? sortLabel : null
                              }
                              onClick={() => sortTable('status')}
                              style={{ width: '80px' }}
                            >
                              Status
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'created_datetime'
                                  ? sortLabel
                                  : null
                              }
                              onClick={() => sortTable('created_datetime')}
                              style={{ width: '160px' }}
                            >
                              Signup timestamp
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'customer_type'
                                  ? sortLabel
                                  : null
                              }
                              onClick={() => sortTable('customer_type')}
                              style={{ width: '110px' }}
                            >
                              Type
                            </Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {activeCustomersWithNoLances.map((d, idx) => (
                            <Table.Row
                              key={idx}
                              onClick={() => {
                                copyToClipboard(d.id);
                                history.push(`/admin/customer/${d.id}`);
                              }}
                            >
                              {currentUserIsDebug ? (
                                <Table.Cell>{d.id}</Table.Cell>
                              ) : (
                                ''
                              )}
                              <Table.Cell>{d.forename}</Table.Cell>
                              <Table.Cell>{d.surname}</Table.Cell>
                              <Table.Cell>{d.email}</Table.Cell>
                              <Table.Cell>{d.registrations}</Table.Cell>
                              <Table.Cell>
                                <a href={'tel:' + d.mob_tel_no}>
                                  {d.mob_tel_no}
                                </a>
                              </Table.Cell>
                              <Table.Cell>{d.status}</Table.Cell>
                              <Table.Cell>
                                {dbUTCDatetimeToLocal(d.created_datetime)}
                              </Table.Cell>
                              <Table.Cell>
                                {brandedCustomerType(d.customer_type)}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </Grid>
                  </Tab.Pane>
                )
              }
            : {},
          currentUserCan('ManageCustomer')
            ? {
                menuItem: 'Duplicate Accounts',
                render: () => (
                  <Tab.Pane attached={false}>
                    <Grid>
                      <Table sortable celled fixed>
                        <Table.Header>
                          <Table.Row>
                            {currentUserIsDebug ? (
                              <Table.HeaderCell style={{ width: '120px' }}>
                                ID
                              </Table.HeaderCell>
                            ) : (
                              ''
                            )}
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'forename' ? sortLabel : null
                              }
                              onClick={() => sortTable('forename')}
                              style={{ width: '120px' }}
                            >
                              Forename
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'surname' ? sortLabel : null
                              }
                              onClick={() => sortTable('surname')}
                              style={{ width: '120px' }}
                            >
                              Surname
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={sortColumn === 'email' ? sortLabel : null}
                              onClick={() => sortTable('email')}
                              style={{ width: '130px' }}
                            >
                              Email
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'registrations'
                                  ? sortLabel
                                  : null
                              }
                              onClick={() => sortTable('registrations')}
                              style={{ width: '110px' }}
                            >
                              Registrations
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'mob_tel_no' ? sortLabel : null
                              }
                              onClick={() => sortTable('mob_tel_no')}
                              style={{ width: '120px' }}
                            >
                              Mobile no.
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'status' ? sortLabel : null
                              }
                              onClick={() => sortTable('status')}
                              style={{ width: '80px' }}
                            >
                              Status
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'created_datetime'
                                  ? sortLabel
                                  : null
                              }
                              onClick={() => sortTable('created_datetime')}
                              style={{ width: '160px' }}
                            >
                              Signup timestamp
                            </Table.HeaderCell>
                            <Table.HeaderCell
                              sorted={
                                sortColumn === 'customer_type'
                                  ? sortLabel
                                  : null
                              }
                              onClick={() => sortTable('customer_type')}
                              style={{ width: '110px' }}
                            >
                              Type
                            </Table.HeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {duplicateCustomers.map((d, idx) => (
                            <Table.Row
                              key={idx}
                              onClick={() => {
                                copyToClipboard(d.id);
                                history.push(`/admin/customer/${d.id}`);
                              }}
                            >
                              {currentUserIsDebug ? (
                                <Table.Cell>{d.id}</Table.Cell>
                              ) : (
                                ''
                              )}
                              <Table.Cell>{d.forename}</Table.Cell>
                              <Table.Cell>{d.surname}</Table.Cell>
                              <Table.Cell>{d.email}</Table.Cell>
                              <Table.Cell>{d.registrations}</Table.Cell>
                              <Table.Cell>
                                <a href={'tel:' + d.mob_tel_no}>
                                  {d.mob_tel_no}
                                </a>
                              </Table.Cell>
                              <Table.Cell>{d.status}</Table.Cell>
                              <Table.Cell>
                                {dbUTCDatetimeToLocal(d.created_datetime)}
                              </Table.Cell>
                              <Table.Cell>
                                {brandedCustomerType(d.customer_type)}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table>
                    </Grid>
                  </Tab.Pane>
                )
              }
            : {}
        ]}
      />
    </Segment>
  );
};
